// ImageUpload — herbruikbaar component voor R2-foto uploads (v3.2.58).
// Eerst geïntroduceerd voor productfoto's; later ook voor klantprojectfoto's
// (5 per project) en eventueel reseller logos.
//
// Werking:
//   • file picker → preview → uploadt direct naar R2 via adminUploadImage
//   • returns de URL via onUploaded callback zodat de parent het in z'n form
//     state kan opslaan
//   • toont preview + verwijder-knop wanneer al een upload bestaat

import { useState, useRef } from 'react';
import { Upload, Image as ImageIcon, X, Loader2, AlertCircle } from 'lucide-react';
import { adminUploadImage, resolveUploadUrl } from '../lib/api';

interface ImageUploadProps {
  /** Bestaande URL — toont preview, verbergt picker. */
  value: string | null;
  /** Callback met de nieuwe URL na succesvolle upload, of null bij verwijderen. */
  onChange: (url: string | null) => void;
  /** R2 folder (bv. 'products', 'projects', 'logos'). */
  folder?: string;
  /** Voorbeeld-tekst onder de picker. */
  placeholder?: string;
  /** Optionele aspect-ratio class voor preview. */
  previewClass?: string;
}

export function ImageUpload({
  value, onChange, folder = 'products',
  placeholder = 'Sleep een foto of klik om te kiezen',
  previewClass = 'h-32 w-32 object-cover rounded-lg border border-gray-200',
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const result = await adminUploadImage(file, folder);
      onChange(result.url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void upload(file);
    e.target.value = '';  // reset zodat dezelfde file opnieuw kan
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) void upload(file);
  };

  if (value) {
    return (
      <div className="relative inline-block">
        <img src={resolveUploadUrl(value)} alt="" className={previewClass} />
        <button
          type="button"
          onClick={() => onChange(null)}
          className="absolute -top-2 -right-2 p-1 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-md"
          title="Foto verwijderen"
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={e => e.preventDefault()}
        disabled={uploading}
        className="w-full px-3 py-4 border-2 border-dashed border-gray-300 hover:border-[#c47a3a] hover:bg-[#faf7f3] rounded-lg text-sm text-[#8a7e74] transition-colors flex flex-col items-center gap-1.5 disabled:opacity-50"
      >
        {uploading ? (
          <>
            <Loader2 className="animate-spin text-[#c47a3a]" size={20} />
            <span className="text-xs">Bezig met uploaden…</span>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5 text-[#c47a3a]">
              <ImageIcon size={18} />
              <Upload size={14} />
            </div>
            <span className="text-xs">{placeholder}</span>
            <span className="text-[10px] text-[#8a7e74]">JPG, PNG of WebP — max 3 MB</span>
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
      />
      {error && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
          <AlertCircle size={12} /> {error}
        </div>
      )}
    </div>
  );
}
